import React, { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { View, ScrollView, TouchableOpacity } from 'react-native';
import { Components, Theme, Utils } from '@oneamerica/dxp-ui-components';
import responsiveStyle from './style';
import { Table, TableWrapper, Cell } from 'react-native-reanimated-table';
import { truncatedTextWithLineBreaks } from '../../../Home/helpers';
import { formatCurrency, formatDate } from '../../../../utils';
import { useDispatch, useSelector } from 'react-redux';
import { GET_RECENT_TRX_HISTORY_DATA } from '../../../../redux/recentTrxHistory/types';
import BreakpointConstants from '../../../../constants/BreakpointConstants';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useFocusEffect } from '@react-navigation/native';
import ColorConstants from '../../../../constants/ColorConstants';

const RecentTransactionHistoryCard = () => {
  const { width } = useContext(Theme.ResponsiveContext);
  const isMobile = !BreakpointConstants.isMD(width);
  const isTablet = !BreakpointConstants.isLG(width);

  const { chevronLeft, chevronRight } = Utils.icons;

  const dispatch = useDispatch();

  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });

  const { ...policyData } = useSelector((state: any) => state.policies);
  const recentTrxHistoryData = useSelector(
    (state: any) => state.recentTrxHistory,
  );
  const allowFinancialActivities =
    policyData.getPoliciesListData?.data?.policySummary?.policyHighlights[0]
      ?.accountConfig?.allowFinancialActivities;

  const truncateCharLimit = 30;
  const style = responsiveStyle();

  const itemsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);

  const getCellWidth = () => {
    if (isMobile) {
      return (width - 32 - 32) / 2;
    } else if (!isMobile && isTablet) {
      return (width - 48 - 48) / 6;
    }
    return 200;
  };

  /** Reset sort state on component mount (tab change) */
  useEffect(() => {
    setSortConfig({ key: null, direction: 'asc' });
  }, []);

  /** Reset sort state when navigating back to this screen */
  useFocusEffect(
    useCallback(() => {
      setSortConfig({ key: null, direction: 'asc' });
    }, [])
  );

  /** Only call the API if allowFinancialActivities is true */
  useEffect(() => {
    if (allowFinancialActivities) {
      dispatch({
        type: GET_RECENT_TRX_HISTORY_DATA,
        payload: {
          token: policyData?.token,
          policyNumber:
            policyData?.getPoliciesListData?.data?.policySummary?.account
              ?.number,
        },
      });
    }
  }, [
    allowFinancialActivities,
    dispatch,
    policyData?.token,
    policyData?.getPoliciesListData?.data?.policySummary?.account?.number,
  ]);

  /** Reset sort state when policy changes or component unmounts */
  useEffect(() => {
    return () => {
      setSortConfig({ key: null, direction: 'asc' });
    };
  }, [policyData?.getPoliciesListData?.data?.policySummary?.account?.number]);

  /** Reset sort state when data changes */
  useEffect(() => {
    setSortConfig({ key: null, direction: 'asc' });
  }, [recentTrxHistoryData?.data?.financials]);

  // get financials directly from selector; normalize to an array
  const financials: Array<any> = useMemo(() => {
    if (
      recentTrxHistoryData?.data?.financials &&
      Array.isArray(recentTrxHistoryData.data.financials)
    ) {
      return recentTrxHistoryData.data.financials;
    }
    if (recentTrxHistoryData?.error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching payment data:', recentTrxHistoryData.error);
    }
    return [];
  }, [recentTrxHistoryData]);

  const sortData = (data: any[]) => {
    if (!sortConfig.key) return data;

    const sortedData = [...data].sort((a, b) => {
      const key = sortConfig.key as string;
      let aVal = a[key];
      let bVal = b[key];

      // Handle different data types
      if (key === 'transactionDate') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      } else if (key === 'grossAmount' || key === 'netAmount') {
        aVal = Number.parseFloat(aVal) || 0;
        bVal = Number.parseFloat(bVal) || 0;
      } else {
        // String comparison
        aVal = String(aVal).toLowerCase();
        bVal = String(bVal).toLowerCase();
      }

      if (aVal < bVal) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aVal > bVal) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return sortedData;
  };

  const sortedFinancials = useMemo(() => {
    return sortData(financials);
  }, [financials, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  };

  const getSortIndicator = (key: string) => {
    const isActive = sortConfig.key === key;
    
    const upArrowColor = !isActive
      ? ColorConstants.oaBlueTint
      : sortConfig.direction === 'asc'
      ? ColorConstants.oaBlueTint
      : '#CCCCCC';
    
    const downArrowColor = !isActive
      ? ColorConstants.oaBlueTint
      : sortConfig.direction === 'desc'
      ? ColorConstants.oaBlueTint
      : '#CCCCCC';
    
    return (
      <View style={{ marginLeft: 4, justifyContent: 'center' }}>
        <FontAwesomeIcon
          icon={Utils.icons.caretDown}
          size={10}
          color={upArrowColor}
          style={{
            marginBottom: -2,
            transform: [{ rotate: '180deg' }], 
            //@ts-ignore
            outline: 'none',
          }}
        />
        <FontAwesomeIcon
          icon={Utils.icons.caretDown}
          size={10}
          color={downArrowColor}
          style={{
            marginTop: -2, 
            //@ts-ignore
            outline: 'none',
          }}
        />
      </View>
    );
  };

  const createTableCell = (index, data, columnKey?: string) => {
    return (
      <Cell
        style={[
          index === 0 ? style.tableHeadingCell1 : style.tableCell1,
          { width: getCellWidth() },
        ]}
        data={data}
        textStyle={
          index === 0 ? style.tableHeadingCellText : style.tableCellText1
        }
      />
    );
  };

  const createSortableHeaderCell = (key: string, label: string) => {
    return (
      <TouchableOpacity
        onPress={() => handleSort(key)}
        style={[
          style.tableHeadingCell1,
          { width: getCellWidth(), flexDirection: 'row', alignItems: 'center' },
        ]}
      >
        <Components.Text.Body style={style.tableHeadingCellText}>
          {label}
        </Components.Text.Body>
        {getSortIndicator(key)}
      </TouchableOpacity>
    );
  };

  const totalPages = Math.ceil(sortedFinancials.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const getCurrentShowingHeaderText = () => {
    return `Showing ${startIndex + 1} to ${endIndex > sortedFinancials.length ? sortedFinancials.length : endIndex} of ${sortedFinancials.length} entries`;
  };

  const getData = () => {
    const data = sortedFinancials.slice(startIndex, endIndex);
    data.unshift({
      transactionDate: 'Transaction Date',
      transactionType: 'Transaction Type',
      transactionDescription: 'Transaction Description',
      grossAmount: 'Gross Amount',
      netAmount: 'Net Amount',
      status: 'Transaction Status',
    });
    return data;
  };

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handleClick = (page) => {
    setCurrentPage(page);
  };

  const Pagination = () => {
    if (sortedFinancials.length === 0) {
      return null;
    }

    return (
      <View className="mt-4">
        <Components.Text style={style.font14}>
          {getCurrentShowingHeaderText()}
        </Components.Text>
        <View className="mt-4 items-center" style={style.flexRow}>
          <TouchableOpacity
            onPress={handlePrev}
            className="mr-2 items-center"
            style={style.flexRow}
          >
            <FontAwesomeIcon icon={chevronLeft as IconProp} size={14} />
            <Components.Text className="ml-1" style={style.font14}>
              Prev
            </Components.Text>
          </TouchableOpacity>
          {Array.from({ length: totalPages }, (_v, index) => {
            return (
              <TouchableOpacity
                key={index}
                onPress={() => handleClick(index + 1)}
                className="mx-2"
              >
                <Components.Text
                  style={[
                    style.font14,
                    currentPage === index + 1
                      ? style.selectedPageText
                      : undefined,
                  ]}
                >
                  {index + 1}
                </Components.Text>
              </TouchableOpacity>
            );
          })}
          <TouchableOpacity
            onPress={handleNext}
            className="ml-2 items-center"
            style={style.flexRow}
          >
            <Components.Text
              className="mr-1"
              style={[style.font14, style.fontBold]}
            >
              Next
            </Components.Text>
            <FontAwesomeIcon icon={chevronRight as IconProp} size={14} />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <View className="lg:flex-row">
      <View className="w-full p-0 mt-8">
        <View style={style.cardShadow}>
          <View style={style.cardHeader}>
            <Components.Text.H3 style={[style.cardHeaderTitle, style.flex]}>
              Recent Transaction History
            </Components.Text.H3>
          </View>
          <View className="mt-4" style={style.horizontalDivider} />
          <Pagination />
          <ScrollView horizontal={true}>
            <View className="mt-4">
              <Table>
                {sortedFinancials?.length > 0
                  ? getData()?.map((item: any, index: number) => (
                      <TableWrapper
                        key={`row-${item.id}_${index}`}
                        style={{
                          flexDirection: 'row',
                          backgroundColor:
                            index % 2 === 0 ? '#FFFFFF' : '#F3F3F3',
                        }}
                      >
                        {index === 0 ? (
                          <>
                            <TouchableOpacity
                              onPress={() => handleSort('transactionDate')}
                              style={[
                                style.tableHeadingCell,
                                { width: getCellWidth(), flexDirection: 'row', alignItems: 'center' },
                              ]}
                            >
                              <Components.Text.Body
                                style={style.tableHeadingCellText}
                              >
                                {item.transactionDate}
                              </Components.Text.Body>
                              {getSortIndicator('transactionDate')}
                            </TouchableOpacity>
                            {createSortableHeaderCell(
                              'transactionType',
                              item.transactionType,
                            )}
                            {createSortableHeaderCell(
                              'transactionDescription',
                              item.transactionDescription,
                            )}
                            {createSortableHeaderCell(
                              'grossAmount',
                              item.grossAmount,
                            )}
                            {createSortableHeaderCell(
                              'netAmount',
                              item.netAmount,
                            )}
                            {createSortableHeaderCell('status', item.status)}
                          </>
                        ) : (
                          <>
                            <Cell
                              width={getCellWidth()}
                              style={[style.tableCell2]}
                              data={truncatedTextWithLineBreaks(
                                formatDate(item.transactionDate, 'MM/DD/YYYY'),
                                truncateCharLimit,
                              )}
                              textStyle={style.tableCellText}
                            />
                            {createTableCell(index, item.transactionType)}
                            {createTableCell(
                              index,
                              item.transactionDescription,
                            )}
                            {createTableCell(
                              index,
                              formatCurrency(item.grossAmount),
                            )}
                            {createTableCell(
                              index,
                              formatCurrency(item.netAmount),
                            )}
                            {createTableCell(index, item.status)}
                          </>
                        )}
                      </TableWrapper>
                    ))
                  : null}
              </Table>
            </View>
          </ScrollView>
          {!(sortedFinancials?.length > 0) && (
            <View style={style.cardContent}>
              <Components.Text.Body style={style.placeholder}>
                No transactions available
              </Components.Text.Body>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export default RecentTransactionHistoryCard;
